// Unified Deal Pipeline — one row per Person (Deal) with nested quotes.
// Mirrored under both Sales & Orders › Deal Pipeline and CRM › Deal Pipeline.
// UI pattern: QuoteOverview-style table + horizontal chip filters + optional
// Kanban board (10 canonical Talmia stages). Row expand → nested quote list.
import { useCan } from "../../hooks/useAreaLevel";
import { useEffect, useLayoutEffect, useMemo, useState, useCallback, useRef, createContext, useContext } from "react";
import { useWindowVirtualizer } from "@tanstack/react-virtual";
import { useQueryClient, isCancelledError } from "@tanstack/react-query";
import { useAccountManagers } from "../../hooks/useAccountManagers";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import {
  GitBranch, Search, Filter, LayoutGrid, Table as TableIcon,
  ChevronDown, ChevronRight, ChevronLeft, ArrowUpDown, ArrowUp, ArrowDown, Loader2, RefreshCw,
  AlertTriangle, Package2, FileText, ExternalLink, Crown, ShieldCheck,
  MapPin, X, Plus, History, FilePlus, Activity, StickyNote, Paperclip, Archive,
  CheckSquare, Square, Layers, Clock, Flame, Zap, Eye, UserCheck,
} from "lucide-react";
import Layout from "../../components/Layout";
import BufferProgressBar from "../../components/BufferProgressBar";
import { useTheme } from "../../context/ThemeContext";
import { useAuth } from "../../context/AuthContext";
import SavedViewChips, { useSavedViews } from "../../components/SavedViewChips";
import DealStatsStrip from "../../components/DealStatsStrip";
import MultiSelectDropdown from "../../components/dev-tracker/MultiSelectDropdown";
import { normalizeDateFilter, isDateFilterActive, dateFilterLabel, applyDateFilterParams, EMPTY_DATE_FILTER } from "../../lib/dateRange";
import DateRangeFilter from "../../components/DateRangeFilter";

const DEAL_DATE_FIELDS = [
  { value: "", label: "Last activity" },
  { value: "updated", label: "Last updated" },
  { value: "last_order", label: "Last order" },
];
import Person360Overlay from "../../components/Person360Overlay";
import { NotesPeekProvider, NotesButton } from "../../components/notes/NotesPeek";
import InfiniteSentinel from "../../components/InfiniteSentinel";
import LiveCount from "../../components/LiveCount";
import { OwnerChip } from "../../components/OwnerChip";
import { OwnerAssignChip, useCanEditAm } from "../../components/OwnerAssignChip";
import QuotePeekModal from "../../components/QuotePeekModal";
import ActivityCell from "../../components/activity/ActivityCell";
import useActivitySummaries from "../../components/activity/useActivitySummaries";
import { toast } from "sonner";
import { createPortal } from "react-dom";
import BulkActivityModal from "../../components/activity/BulkActivityModal";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Context carrying the "open read-only quote peek" callback so nested cards/
// rows can trigger it without deep prop-drilling.
const QuotePeekCtx = createContext(null);

/* ─── tiny helpers ─── */
const GBP = (n) => (n === null || n === undefined || Number.isNaN(Number(n)))
  ? "—"
  : "£" + Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 });
const GBP_FULL = (n) => (n === null || n === undefined || Number.isNaN(Number(n)))
  ? "—"
  : "£" + Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const relTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(+d)) return "—";
  const days = Math.round((Date.now() - +d) / 86400000);
  if (days === 0) return "today";
  if (days === 1) return "1d";
  if (days < 30) return `${days}d`;
  if (days < 365) return `${Math.round(days / 30)}mo`;
  return `${Math.round(days / 365)}y`;
};
const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(+d) ? "—" : d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

/* ─── stage colour tokens ─── */
const STAGE_COLOR = {
  slate: "text-slate-300 border-slate-500/40 bg-slate-500/[0.06]",
  fuchsia: "text-fuchsia-300 border-fuchsia-500/40 bg-fuchsia-500/[0.06]",
  sky: "text-sky-300 border-sky-500/40 bg-sky-500/[0.06]",
  purple: "text-purple-300 border-purple-500/40 bg-purple-500/[0.06]",
  orange: "text-orange-300 border-orange-500/40 bg-orange-500/[0.06]",
  amber: "text-amber-300 border-amber-500/40 bg-amber-500/[0.06]",
  yellow: "text-yellow-200 border-yellow-500/40 bg-yellow-500/[0.06]",
  emerald: "text-emerald-300 border-emerald-500/40 bg-emerald-500/[0.06]",
  rose: "text-rose-300 border-rose-500/40 bg-rose-500/[0.06]",
  cyan: "text-cyan-300 border-cyan-500/40 bg-cyan-500/[0.06]",
};
const stageChipCls = (color) => STAGE_COLOR[color] || STAGE_COLOR.slate;

/* ─── tier chip ─── */
const TIER_STYLE = {
  BLACK:      { cls: "bg-black text-white border-white/25",                    Icon: Crown },
  PLATINUM:   { cls: "bg-cyan-500/15 text-cyan-300 border-cyan-500/40",         Icon: Crown },
  GOLD:       { cls: "bg-amber-500/15 text-amber-300 border-amber-500/40",      Icon: Crown },
  SILVER:     { cls: "bg-slate-400/15 text-slate-200 border-slate-400/40",      Icon: ShieldCheck },
  BRONZE:     { cls: "bg-orange-800/25 text-orange-300 border-orange-700/50",   Icon: ShieldCheck },
};
function TierChip({ tier, small = false }) {
  if (!tier) return null;
  const raw = String(tier).toUpperCase();
  const key = raw.split(/[\s-]/)[0];
  const s = TIER_STYLE[key] || { cls: "bg-white/5 text-slate-300 border-white/10", Icon: Crown };
  const { Icon } = s;
  return (
    <span
      data-testid={`tier-chip-${key}`}
      className={`inline-flex items-center gap-1 rounded font-mono uppercase tracking-widest border ${s.cls} ${small ? "px-1 py-0 text-[9px]" : "px-1.5 py-0.5 text-[9.5px]"}`}
    >
      <Icon className="w-2.5 h-2.5" /> {tier}
    </span>
  );
}

const TIER_OPTS = ["BLACK", "PLATINUM", "GOLD", "SILVER", "BRONZE"];

/* ─── sortable table header cell (Item 3) ─── */
function SortTh({ label, sortKey, activeKey, dir, onSort, align = "left" }) {
  const active = activeKey === sortKey;
  return (
    <th className={`py-2 pr-3 select-none ${align === "right" ? "text-right" : "text-left"}`}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        data-testid={`deals-th-${sortKey}`}
        className={`inline-flex items-center gap-1 font-mono uppercase tracking-widest hover:text-white transition-colors ${active ? "text-orange-100" : "text-orange-200/80"} ${align === "right" ? "flex-row-reverse" : ""}`}
        title={`Sort by ${label}`}
      >
        {label}
        {active
          ? (dir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)
          : <ArrowUpDown className="w-3 h-3 opacity-30" />}
      </button>
    </th>
  );
}

/* ─── nested quote row (shown when row expanded) ─── */
function QuoteMiniRow({ q, onArchived }) {
  const navigate = useNavigate();
  const archive = async (e) => {
    e.stopPropagation();
    if (!window.confirm(`Archive quote ${q.quote_ref}? It will be hidden from active lists.`)) return;
    try {
      await axios.post(`${API}/quotes/${q.id}/archive`, { reason: "Archived from Deal Pipeline row" }, { withCredentials: true });
      onArchived?.(q.id);
    } catch { /* toast handled by parent if it wants */ }
  };
  return (
    <div
      onClick={(e) => { e.stopPropagation(); navigate(`/quotes/${q.id}/edit`); }}
      data-testid={`deal-quote-${q.quote_ref || q.id}`}
      className="flex items-center gap-3 px-3 py-2 rounded-md border border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.04] hover:border-white/15 cursor-pointer transition-colors"
    >
      <FileText className="w-3.5 h-3.5 text-orange-300 shrink-0" />
      <span className="font-mono text-[11.5px] text-orange-200">{q.quote_ref || `#${q.id.slice(-6)}`}</span>
      <span className="text-[11px] text-slate-400 uppercase tracking-widest font-mono">{q.status}</span>
      {q.project && <span className="text-[11px] text-amber-200/90 truncate max-w-[40%]" title={q.project} data-testid={`deal-quote-project-${q.quote_ref || q.id}`}>{q.project}</span>}
      <span className="ml-auto tabular-nums text-[12px] text-white">{GBP_FULL(q.total)}</span>
      <span className="text-[10px] text-slate-500 font-mono">{relTime(q.updated)}</span>
      <button
        type="button"
        onClick={archive}
        className="text-slate-500 hover:text-amber-300 p-1 -m-1"
        title={`Archive ${q.quote_ref}`}
        data-testid={`deal-quote-archive-${q.quote_ref || q.id}`}
      >
        <Archive className="w-3 h-3" />
      </button>
      <ExternalLink className="w-3 h-3 text-slate-500" />
    </div>
  );
}

/* ─── Pipedrive history panel (lazy-fetched on row expand) ─── */
function stripHtml(s) {
  if (!s) return "";
  return String(s).replace(/<br\s*\/?>/gi, " ").replace(/<[^>]+>/g, "").replace(/\s+/g, " ").trim();
}
function fmtBytes(n) {
  const num = Number(n);
  if (!Number.isFinite(num) || num <= 0) return null;
  const units = ["B", "KB", "MB", "GB"];
  let i = 0, v = num;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

function PipedriveHistoryPanel({ personId }) {
  const [state, setState] = useState({ loading: true, error: null, data: null });
  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, error: null, data: null });
    axios
      .get(`${API}/deals/${personId}/pipedrive`, { withCredentials: true })
      .then((r) => { if (!cancelled) setState({ loading: false, error: null, data: r.data }); })
      .catch((e) => { if (!cancelled) setState({ loading: false, error: e.response?.data?.detail || "Failed to load", data: null }); });
    return () => { cancelled = true; };
  }, [personId]);

  if (state.loading) {
    return (
      <div className="flex items-center gap-2 text-[11px] text-slate-500 font-mono px-3 py-3">
        <Loader2 className="w-3 h-3 animate-spin" /> Loading Pipedrive history…
      </div>
    );
  }
  if (state.error) {
    return (
      <div className="text-[11px] text-rose-300 font-mono px-3 py-2" data-testid={`pd-history-error-${personId}`}>
        {state.error}
      </div>
    );
  }
  const d = state.data;
  const { activities = [], notes = [], attachments = [], counts = {}, pipedrive = {} } = d || {};
  const isEmpty = activities.length + notes.length + attachments.length === 0;

  // Wave M: DealId is ignored. The "Pipedrive #<deal>" chip is retired; the
  // pipeline id still surfaces below since it's used by the tier resolver.
  const hasLink = !!(pipedrive.pipeline_id);

  return (
    <div className="space-y-2" data-testid={`pd-history-${personId}`}>
      {/* Counts strip */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[9.5px] font-mono uppercase tracking-widest text-purple-300 inline-flex items-center gap-1.5">
          <History className="w-3 h-3" /> Pipedrive history
        </span>
        <span className="inline-flex items-center gap-1 text-[10.5px] font-mono text-purple-200 px-1.5 py-0.5 rounded border border-purple-500/30 bg-purple-500/10">
          <Activity className="w-3 h-3" /> {counts.activities || 0}
        </span>
        <span className="inline-flex items-center gap-1 text-[10.5px] font-mono text-slate-300 px-1.5 py-0.5 rounded border border-white/10 bg-white/[0.03]">
          <StickyNote className="w-3 h-3" /> {counts.notes || 0}
        </span>
        <span className="inline-flex items-center gap-1 text-[10.5px] font-mono text-fuchsia-200 px-1.5 py-0.5 rounded border border-fuchsia-500/30 bg-fuchsia-500/10">
          <Paperclip className="w-3 h-3" /> {counts.attachments || 0}
        </span>
        {hasLink && (
          <span className="text-[10px] font-mono text-slate-500 ml-1" data-testid={`pd-history-pipelineref-${personId}`}>
            Pipedrive pipeline {pipedrive.pipeline_id}
            {pipedrive.stage_id && ` · stage ${pipedrive.stage_id}`}
          </span>
        )}
        <Link
          to={`/crm/persons/${personId}`}
          onClick={(e) => e.stopPropagation()}
          className="ml-auto inline-flex items-center gap-1 text-[10.5px] font-mono text-orange-300 hover:text-orange-200"
          data-testid={`pd-history-view360-${personId}`}
        >
          Open Customer 360 <ExternalLink className="w-3 h-3" />
        </Link>
      </div>

      {isEmpty ? (
        <div className="text-[11px] text-slate-500 font-mono px-3 py-3 border border-dashed border-white/[0.06] rounded">
          No Pipedrive activities, notes or attachments mirrored yet.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {/* Activities */}
          <div className="rounded-md border border-white/[0.06] bg-white/[0.02] p-2.5 space-y-1.5" data-testid={`pd-history-activities-${personId}`}>
            <div className="text-[9.5px] font-mono uppercase tracking-widest text-purple-300 inline-flex items-center gap-1">
              <Activity className="w-3 h-3" /> Activities · {activities.length}
            </div>
            {activities.length === 0 && <div className="text-[11px] text-slate-600 font-mono">None</div>}
            {activities.slice(0, 3).map((a) => (
              <div key={`act-${a.id || a.subject}-${a.created || ""}`} className="text-[11px] leading-snug">
                <div className="text-slate-200 truncate">{a.subject || a.type || "Activity"}</div>
                <div className="text-[10px] text-slate-500 font-mono">
                  {a.type && <span>{a.type}</span>}
                  {a.created && <span> · {relTime(a.created)}</span>}
                  {a.user_name && <span> · {a.user_name}</span>}
                </div>
              </div>
            ))}
            {activities.length > 3 && (
              <div className="text-[10px] text-slate-500 font-mono">+{activities.length - 3} more…</div>
            )}
          </div>

          {/* Notes */}
          <div className="rounded-md border border-white/[0.06] bg-white/[0.02] p-2.5 space-y-1.5" data-testid={`pd-history-notes-${personId}`}>
            <div className="text-[9.5px] font-mono uppercase tracking-widest text-slate-300 inline-flex items-center gap-1">
              <StickyNote className="w-3 h-3" /> Notes · {notes.length}
            </div>
            {notes.length === 0 && <div className="text-[11px] text-slate-600 font-mono">None</div>}
            {notes.slice(0, 3).map((n) => {
              const txt = stripHtml(n.content);
              return (
                <div key={`note-${n.id || (n.created || "") + txt.slice(0, 12)}`} className="text-[11px] leading-snug">
                  <div className="text-slate-300 line-clamp-2">
                    {txt.slice(0, 140)}{txt.length > 140 && "…"}
                  </div>
                  <div className="text-[10px] text-slate-500 font-mono">
                    {n.created && relTime(n.created)}
                    {n.user_name && <span> · {n.user_name}</span>}
                  </div>
                </div>
              );
            })}
            {notes.length > 3 && (
              <div className="text-[10px] text-slate-500 font-mono">+{notes.length - 3} more…</div>
            )}
          </div>

          {/* Attachments */}
          <div className="rounded-md border border-white/[0.06] bg-white/[0.02] p-2.5 space-y-1.5" data-testid={`pd-history-attachments-${personId}`}>
            <div className="text-[9.5px] font-mono uppercase tracking-widest text-fuchsia-300 inline-flex items-center gap-1">
              <Paperclip className="w-3 h-3" /> Attachments · {attachments.length}
            </div>
            {attachments.length === 0 && <div className="text-[11px] text-slate-600 font-mono">None</div>}
            {attachments.slice(0, 3).map((f) => {
              const size = fmtBytes(f.file_size);
              const row = (
                <div className="text-[11px] leading-snug">
                  <div className="text-slate-200 truncate">{f.name || "File"}</div>
                  <div className="text-[10px] text-slate-500 font-mono flex flex-wrap gap-x-2">
                    {f.file_type && <span>{f.file_type}</span>}
                    {size && <span>{size}</span>}
                    {f.added && <span>{relTime(f.added)}</span>}
                  </div>
                </div>
              );
              return f.url ? (
                <a
                  key={`file-${f.id || f.name}-${f.added || ""}`}
                  href={f.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="block hover:bg-white/[0.03] rounded px-1 -mx-1"
                >
                  {row}
                </a>
              ) : (
                <div key={`file-${f.id || f.name}-${f.added || ""}`}>{row}</div>
              );
            })}
            {attachments.length > 3 && (
              <div className="text-[10px] text-slate-500 font-mono">+{attachments.length - 3} more…</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── table row ─── */
// Urgency bucket → tone. Warm 4+, Hot 7+, Critical 9+ (thresholds match backend focus).
function urgencyTone(u) {
  if (u >= 9) return { cls: "bg-red-500/15 text-red-300 border-red-500/30", label: "Critical" };
  if (u >= 7) return { cls: "bg-orange-500/15 text-orange-300 border-orange-500/30", label: "Hot" };
  if (u >= 4) return { cls: "bg-amber-500/15 text-amber-300 border-amber-500/30", label: "Warm" };
  return null;
}

// Compact quote-signal strip for a deal row/card: urgency + ⚡ASAP + build + master ref.
function DealSignals({ deal, compact = false }) {
  const openPeek = useContext(QuotePeekCtx);
  const tone = urgencyTone(deal.urgency || 0);
  const hasAny = tone || deal.asap || deal.build_type || deal.master_quote_ref || deal.project;
  if (!hasAny) return null;
  return (
    <div className={`flex items-center gap-1.5 flex-wrap ${compact ? "mt-1" : "mt-1"}`} data-testid={`deal-signals-${deal.id}`}>
      {deal.project && (
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold border bg-amber-500/10 text-amber-200 border-amber-500/30 truncate max-w-[220px]" title={`Project: ${deal.project}`} data-testid={`deal-project-${deal.id}`}>
          <MapPin className="w-2.5 h-2.5 shrink-0" />{deal.project}
        </span>
      )}
      {tone && (
        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium border ${tone.cls}`} title={`Urgency ${deal.urgency}/10`}>
          <Flame className="w-2.5 h-2.5" />{tone.label} {deal.urgency}
        </span>
      )}
      {deal.asap && (
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium border bg-rose-500/15 text-rose-200 border-rose-500/30" title="ASAP flagged on an open quote">
          <Zap className="w-2.5 h-2.5" />ASAP
        </span>
      )}
      {deal.build_type && (
        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] border bg-white/5 text-slate-300 border-white/10">
          {deal.build_type}
        </span>
      )}
      {deal.master_quote_ref && (
        deal.master_quote_id ? (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); openPeek?.(deal.master_quote_id); }}
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-mono text-teal-300/90 border border-teal-500/30 bg-teal-500/5 hover:bg-teal-500/15 hover:text-teal-200 transition-colors"
            title={`Peek at ${deal.master_quote_ref}${deal.has_live_quote ? "" : " (idle)"}`}
            data-testid={`deal-peek-quote-${deal.id}`}
          >
            <Eye className="w-2.5 h-2.5" />{deal.master_quote_ref}{!deal.has_live_quote && <span className="text-slate-500">(idle)</span>}
          </button>
        ) : (
          <span className="inline-flex items-center gap-1 text-[10px] font-mono text-teal-300/80" title={deal.has_live_quote ? "Active master quote" : "No live quote — most recent shown"}>
            {deal.master_quote_ref}{!deal.has_live_quote && <span className="text-slate-500">(idle)</span>}
          </span>
        )
      )}
    </div>
  );
}

function DealRow({ deal, stages, salesDays = 30, onNavigate, onToggleExpand, onNewQuote, onQuoteArchived, onPeekQuotes, onStageChange, onOwnerChanged, selected, onToggleSelect, expanded, innerRef, dataIndex, activitySummary, onActivitySaved }) {
  const can = useCan("/deals");
  const stage = stages.find((s) => s.key === deal.stage_key);
  const stageColor = stage?.color;
  const stageLabel = stage?.label || "Unmapped";

  return (
    <tbody ref={innerRef} data-index={dataIndex}>
      <tr
        data-testid={`deal-row-${deal.id}`}
        onClick={() => onNavigate(deal.id)}
        className={`border-b border-white/[0.04] hover:bg-white/[0.03] cursor-pointer ${selected ? "bg-teal-500/[0.06]" : ""}`}
      >
        <td className="py-2.5 pl-3 pr-1 w-6" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            onClick={() => onToggleSelect(deal.id)}
            className="w-5 h-5 grid place-items-center rounded text-slate-500 hover:text-teal-300"
            data-testid={`deal-select-${deal.id}`}
            aria-label={selected ? "Deselect" : "Select"}
          >
            {selected ? <CheckSquare className="w-4 h-4 text-teal-300" /> : <Square className="w-4 h-4" />}
          </button>
        </td>
        <td className="py-2.5 pl-1 pr-2 w-8">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onToggleExpand(deal.id); }}
            className="w-5 h-5 grid place-items-center rounded text-slate-500 hover:text-white hover:bg-white/5"
            data-testid={`deal-expand-${deal.id}`}
            aria-label={expanded ? "Collapse" : "Expand"}
          >
            {expanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
          </button>
        </td>
        <td className="py-2.5 pr-3">
          <div className="flex items-center gap-2">
            <div className="text-[13px] font-medium text-white truncate max-w-[240px]">
              {deal.full_name || deal.company || "—"}
            </div>
            <NotesButton personId={deal.id} name={deal.full_name || deal.company} compact />
          </div>
          <div className="text-[11px] text-slate-400 truncate max-w-[280px]">
            {deal.company && <span className="text-slate-500">{deal.company}</span>}
          </div>
          <DealSignals deal={deal} />
        </td>
        <td className="py-2.5 pr-3">
          <TierChip tier={deal.tier} />
        </td>
        <td className="py-2.5 pr-3">
          <select
            value={deal.stage_key || ""}
            onChange={(e) => { e.stopPropagation(); onStageChange(deal.id, e.target.value); }}
            onClick={(e) => e.stopPropagation()}
            disabled={!can("edit")}
            className={`dark-select inline-block px-1.5 py-1 rounded border font-mono uppercase tracking-wider text-[9.5px] hover:bg-white/[0.06] focus:outline-none focus:ring-1 focus:ring-teal-500/50 cursor-pointer ${stageChipCls(stageColor)}`}
            data-testid={`deal-stage-select-${deal.id}`}
            title="Change stage — auto-syncs across the customer 360"
          >
            {!deal.stage_key && <option value="" disabled>— Unmapped —</option>}
            {stages.map((s) => (
              <option key={s.key} value={s.key}>{s.label}</option>
            ))}
          </select>
          {deal.is_rotting && (
            <div className="mt-1 text-[9px] font-mono uppercase tracking-widest text-rose-300 flex items-center gap-1">
              <AlertTriangle className="w-2.5 h-2.5" /> +{deal.rot_days_over}d rot
            </div>
          )}
        </td>
        <td className="py-2.5 pr-3 tabular-nums text-right text-emerald-300 text-[12.5px] font-medium">
          {GBP_FULL(deal.ltv)}
        </td>
        <td className="py-2.5 pr-3 tabular-nums text-right text-[12.5px]" data-testid={`deal-period-sales-${deal.id}`}
          title={`Paid/shipped orders inc VAT, last ${salesDays} days`}>
          {Number(deal[`sales_${salesDays}`] || 0) > 0
            ? <span className="text-lime-300 font-medium">{GBP_FULL(deal[`sales_${salesDays}`])}</span>
            : <span className="text-slate-600">—</span>}
        </td>
        <td className="py-2.5 pr-3 tabular-nums text-right text-[12.5px] text-orange-200" data-testid={`deal-quote-value-${deal.id}`}
          title={deal.latest_quote_ref ? `Latest quote ${deal.latest_quote_ref}` : "No quotes"}>
          {deal.latest_quote_value != null ? GBP_FULL(deal.latest_quote_value) : <span className="text-slate-600">—</span>}
        </td>
        <td className="py-2.5 pr-3 tabular-nums text-right text-slate-400 text-[12px]">
          <span className="inline-flex items-center gap-1"><Package2 className="w-3 h-3 text-slate-500" />{deal.orders_count ?? 0}</span>
        </td>
        <td className="py-2.5 pr-3 tabular-nums text-right text-[12px]">
          <div className="flex items-center gap-1 justify-end">
            <span className={`inline-flex items-center gap-1 ${deal.quote_count ? "text-orange-300" : "text-slate-600"}`}>
              <FileText className="w-3 h-3" />{deal.quote_count}
            </span>
            {(deal.quote_count || 0) > 0 && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onPeekQuotes(deal); }}
                data-testid={`deal-peek-quotes-${deal.id}`}
                className="ml-1 px-2 py-0.5 rounded text-[10px] font-mono uppercase tracking-widest border border-teal-500/40 text-teal-300 hover:bg-teal-500/10"
                title={`View all ${deal.quote_count} quotes for ${deal.full_name}`}
              >View</button>
            )}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onNewQuote(deal.id); }}
              data-testid={`deal-new-quote-${deal.id}`}
              className="ml-1 w-5 h-5 grid place-items-center rounded text-slate-500 hover:text-orange-300 hover:bg-orange-500/10"
              aria-label={`Create quote pre-attached to ${deal.full_name}`}
              title={`New quote for ${deal.full_name}`}
            >
              <FilePlus className="w-3.5 h-3.5" />
            </button>
          </div>
        </td>
        <td className="py-2.5 pr-3 tabular-nums text-right text-[12px] text-sky-300" title="Combined value of this customer's open quotes">
          {deal.open_value ? GBP_FULL(deal.open_value) : <span className="text-slate-600">—</span>}
        </td>
        <td className="py-2.5 pr-3 max-w-[160px]" onClick={(e) => e.stopPropagation()}>
          {can("edit") ? <OwnerAssignChip personId={deal.id} primary={deal.am} onChanged={onOwnerChanged} changed={deal.am_changed} />
            : <span className="text-[11px] text-slate-300" data-testid={`deal-am-readonly-${deal.id}`}>{deal.am || "—"}</span>}
        </td>
        <td className="py-2.5 pr-3" onClick={(e) => e.stopPropagation()}>
          <ActivityCell personId={deal.id} am={deal.am} summary={activitySummary} onSaved={onActivitySaved} readOnly={!can("edit")} />
        </td>
        <td className="py-2.5 pr-3 text-[10.5px] text-slate-500 font-mono">
          {deal.post_code && (
            <span className="inline-flex items-center gap-1"><MapPin className="w-2.5 h-2.5" />{deal.post_code}</span>
          )}
        </td>
        <td className="py-2.5 pr-3 text-[10.5px] text-slate-400 font-mono whitespace-nowrap" title="Last order date">
          {fmtDate(deal.last_order_date)}
        </td>
        <td className="py-2.5 pr-3 text-[10.5px] text-slate-500 font-mono whitespace-nowrap">
          {relTime(deal.last_updated)}
        </td>
      </tr>
      {expanded && (
        <tr className="bg-white/[0.02]" data-testid={`deal-row-${deal.id}-expanded`}>
          <td></td>
          <td colSpan={16} className="py-3 pr-3 space-y-4">
            {/* Quotes list */}
            {deal.quotes.length === 0 ? (
              <div className="flex items-center gap-3 text-[11px] text-slate-500 font-mono px-3 py-4 border border-dashed border-white/[0.06] rounded">
                <span>No quotes yet for this deal — </span>
                <Link
                  to={`/quotes/new?customer=${deal.id}`}
                  onClick={(e) => e.stopPropagation()}
                  data-testid={`deal-new-quote-empty-${deal.id}`}
                  className="text-orange-300 hover:underline inline-flex items-center gap-1"
                >
                  <FilePlus className="w-3 h-3" /> create one
                </Link>
              </div>
            ) : (
              <div className="space-y-1.5 pl-3">
                <div className="text-[9.5px] font-mono uppercase tracking-widest text-slate-500 mb-1">
                  {deal.quotes.length} quote{deal.quotes.length === 1 ? "" : "s"} for this deal
                </div>
                {deal.quotes.map((q) => <QuoteMiniRow key={q.id} q={q} onArchived={onQuoteArchived} />)}
              </div>
            )}

            {/* Pipedrive history (lazy) */}
            <div onClick={(e) => e.stopPropagation()} className="pl-3">
              <PipedriveHistoryPanel personId={deal.id} />
            </div>
          </td>
        </tr>
      )}
    </tbody>
  );
}

/* ─── Kanban (compact — reuses server-side board endpoint) ─── */
const QUOTE_STATUS_TONE = {
  DRAFT: "text-slate-400 bg-white/5 border-white/10",
  SENT: "text-sky-300 bg-sky-500/10 border-sky-500/30",
  WON: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30",
  ACCEPTED: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30",
  LOST: "text-rose-300 bg-rose-500/10 border-rose-500/30",
  EXPIRED: "text-amber-300 bg-amber-500/10 border-amber-500/30",
};
function QuoteStatusPill({ status }) {
  const tone = QUOTE_STATUS_TONE[String(status || "").toUpperCase()] || "text-slate-400 bg-white/5 border-white/10";
  return <span className={`text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded border shrink-0 ${tone}`}>{status || "—"}</span>;
}

function BoardCard({ card, onNavigate, sla }) {
  const [expanded, setExpanded] = useState(false);
  const openPeek = useContext(QuotePeekCtx);
  const stop = (e) => e.stopPropagation();

  const quotes = Array.isArray(card.quotes) ? card.quotes : [];
  const primary = quotes.find((q) => q.id === card.master_quote_id) || quotes[0] || null;
  const rest = quotes.filter((q) => q !== primary);
  const urg = urgencyTone(card.urgency || 0);

  // SLA warm-reply clock — only on AI CX Replied, only when enabled in config.
  let slaBadge = null;
  if (sla?.enabled && card.stage_key === "ai_cx_replied") {
    const since = card.override?.moved_at || card.last_updated;
    const mins = since ? Math.max(0, Math.round((Date.now() - new Date(since).getTime()) / 60000)) : null;
    if (mins != null) {
      const warn = sla.cx_replied_warn_minutes ?? 20;
      const alert = sla.cx_replied_alert_minutes ?? 30;
      const level = mins >= alert ? "alert" : mins >= warn ? "warn" : "ok";
      const disp = mins >= 120 ? `${Math.floor(mins / 60)}h ${mins % 60}m` : `${mins}m`;
      const cls = level === "alert" ? "text-rose-300" : level === "warn" ? "text-amber-300" : "text-emerald-300/90";
      slaBadge = (
        <span className={`inline-flex items-center gap-1 text-[9.5px] font-mono uppercase tracking-widest ${cls} ${level === "alert" ? "animate-pulse" : ""}`}
          data-testid={`board-card-sla-${card.id}`} data-sla-level={level}>
          <Clock className="w-2.5 h-2.5" /> {disp}
        </span>
      );
    }
  }

  return (
    <div
      onClick={() => onNavigate(card.id)}
      data-testid={`board-card-${card.id}`}
      className="group rounded-lg border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] hover:border-teal-500/30 p-2.5 cursor-pointer transition-all"
    >
      {/* 1 · Identity */}
      <div className="flex items-start justify-between gap-1.5">
        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-semibold text-white leading-tight truncate">{card.full_name || card.company || "—"}</div>
          {card.company && card.full_name && (
            <div className="text-[11px] text-slate-400 leading-tight truncate">{card.company}</div>
          )}
        </div>
        <TierChip tier={card.tier} small />
      </div>

      {/* 2 · Owner + value */}
      <div className="flex items-center justify-between gap-2 mt-1.5 pt-1.5 border-t border-white/[0.06]">
        {card.am ? <OwnerChip primary={card.am} /> : <span className="text-[10px] font-mono text-slate-600 uppercase tracking-widest">Unassigned</span>}
        <span className="shrink-0 inline-flex items-baseline gap-1">
          <span className="text-[8.5px] font-mono uppercase tracking-widest text-slate-500">LTV</span>
          <span className="tabular-nums font-mono font-bold text-[12.5px] text-emerald-300">{GBP_FULL(card.ltv)}</span>
        </span>
      </div>

      {/* 3 · Signals (urgency · asap · build) */}
      {(urg || card.asap || card.build_type || card.project) && (
        <div className="flex items-center gap-1 flex-wrap mt-1.5" data-testid={`deal-signals-${card.id}`}>
          {card.project && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9.5px] font-semibold border bg-amber-500/10 text-amber-200 border-amber-500/30 truncate max-w-full" title={`Project: ${card.project}`} data-testid={`deal-card-project-${card.id}`}>
              <MapPin className="w-2.5 h-2.5 shrink-0" />{card.project}
            </span>
          )}
          {urg && (
            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9.5px] font-medium border ${urg.cls}`} title={`Urgency ${card.urgency}/10`}>
              <Flame className="w-2.5 h-2.5" />{urg.label} {card.urgency}
            </span>
          )}
          {card.asap && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9.5px] font-medium border bg-rose-500/15 text-rose-200 border-rose-500/30">
              <Zap className="w-2.5 h-2.5" />ASAP
            </span>
          )}
          {card.build_type && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9.5px] border bg-white/5 text-slate-300 border-white/10 truncate max-w-[120px]">{card.build_type}</span>
          )}
        </div>
      )}

      {/* 4 · Permanent primary quote island */}
      <div className="mt-2 rounded-md border border-white/[0.08] bg-black/20 p-2" onClick={stop}>
        {primary ? (
          <>
            <div className="flex items-center justify-between gap-2">
              <span className="font-mono font-bold text-[11px] text-orange-300 tracking-wide truncate">{primary.quote_ref}</span>
              <QuoteStatusPill status={primary.status} />
            </div>
            <div className="flex items-center justify-between gap-2 mt-1.5">
              <span className="font-mono font-bold tabular-nums text-[12px] text-white">{GBP_FULL(primary.total)}</span>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); openPeek?.(primary.id); }}
                className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded border border-teal-500/30 bg-teal-500/10 text-teal-200 hover:bg-teal-500/20 text-[10px] font-mono uppercase tracking-wider transition-colors"
                title={`Peek at ${primary.quote_ref}`}
                data-testid={`board-card-quote-peek-${primary.id}`}
              >
                <Eye className="w-3 h-3" /> Peek
              </button>
            </div>
            {rest.length > 0 && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}
                className="mt-1.5 w-full text-[9.5px] font-mono uppercase tracking-widest text-slate-500 hover:text-teal-300 flex items-center justify-center gap-1"
                data-testid={`board-card-expand-${card.id}`}
              >
                {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                {expanded ? "Hide" : `+${rest.length} more quote${rest.length > 1 ? "s" : ""}`}
              </button>
            )}
            {expanded && rest.length > 0 && (
              <div className="mt-1.5 pt-1.5 border-t border-white/[0.06] space-y-1">
                {rest.slice(0, 6).map((q) => (
                  <div key={q.id} className="flex items-center gap-2 text-[10.5px]" data-testid={`board-card-secondary-quote-${q.id}`}>
                    <span className="font-mono text-orange-200 truncate">{q.quote_ref}</span>
                    {q.project && <span className="text-amber-200/90 truncate max-w-[45%]" title={q.project}>{q.project}</span>}
                    <QuoteStatusPill status={q.status} />
                    <span className="ml-auto tabular-nums font-mono text-slate-200">{GBP_FULL(q.total)}</span>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); openPeek?.(q.id); }}
                      className="shrink-0 p-0.5 rounded hover:bg-teal-500/20 text-teal-300/70 hover:text-teal-200"
                      title={`Peek at ${q.quote_ref}`}
                      data-testid={`board-card-secondary-peek-${q.id}`}
                    >
                      <Eye className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="text-[10.5px] text-slate-500 italic text-center py-0.5">No quote yet</div>
        )}
      </div>

      {/* 5 · Footer — timing + rot/SLA */}
      <div className="flex items-center justify-between gap-2 mt-2 pt-1.5 border-t border-white/[0.06] text-[10px] font-mono">
        <span className="inline-flex items-center gap-2 text-slate-500"><span className="inline-flex items-center gap-1"><Clock className="w-2.5 h-2.5" />{relTime(card.last_updated)}</span>
          <NotesButton personId={card.id} name={card.full_name || card.company} /></span>
        <span className="inline-flex items-center gap-1.5">
          {slaBadge}
          {card.is_rotting && (
            <span className="inline-flex items-center gap-1 text-rose-300 uppercase tracking-widest text-[9.5px]" title={`Rotting ${card.rot_days_over} days over`}>
              <AlertTriangle className="w-2.5 h-2.5" />+{card.rot_days_over}d
            </span>
          )}
        </span>
      </div>
    </div>
  );
}
function BoardView({ deals, stages, onNavigate, onStageChange, containerRef, sla }) {
  const grouped = useMemo(() => {
    const map = Object.fromEntries(stages.map((s) => [s.key, []]));
    map["_unmapped"] = [];
    for (const d of deals) {
      const k = d.stage_key && map[d.stage_key] ? d.stage_key : "_unmapped";
      map[k].push(d);
    }
    return map;
  }, [deals, stages]);

  const cols = [...stages, { key: "_unmapped", label: "Unmapped", color: "slate", probability: 0 }];
  const [dragOver, setDragOver] = useState(null);

  // Lazy-load per column — render top 25 cards first, "Show N more" pulls the
  // next 25 without any network fetch (deals list already in memory).
  // Reset whenever the underlying `deals` array changes (filter / paging /
  // new fetch) so a filtered view doesn't inherit an oversized cap.
  const INITIAL_PER_COL = 25;
  const [visibleByCol, setVisibleByCol] = useState({});
  useEffect(() => { setVisibleByCol({}); }, [deals]);
  const bumpCol = useCallback((key) => {
    setVisibleByCol((prev) => ({ ...prev, [key]: (prev[key] || INITIAL_PER_COL) + INITIAL_PER_COL }));
  }, []);

  const onDragStart = (e, deal) => {
    e.dataTransfer.setData("text/plain", JSON.stringify({ id: deal.id, from: deal.stage_key }));
    e.dataTransfer.effectAllowed = "move";
  };
  const onDrop = (e, toKey) => {
    e.preventDefault();
    setDragOver(null);
    try {
      const { id, from } = JSON.parse(e.dataTransfer.getData("text/plain") || "{}");
      if (!id || !toKey || toKey === "_unmapped" || from === toKey) return;
      onStageChange?.(id, toKey);
    } catch { /* silent */ }
  };
  const onDragOver = (e, key) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; setDragOver(key); };

  return (
    <div ref={containerRef} className="-mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 overflow-x-auto pb-4">
      <div className="flex gap-3 min-w-max" data-testid="deal-board">
        {cols.map((s) => {
          const items = grouped[s.key] || [];
          const total = items.reduce((sum, d) => sum + Number(d.ltv || 0), 0);
          const dotColor = stageChipCls(s.color).split(" ")[0].replace("text-", "bg-").replace(/-\d00$/, "-400");
          const isDragOver = dragOver === s.key;
          const cap = visibleByCol[s.key] || INITIAL_PER_COL;
          const visibleItems = items.slice(0, cap);
          const hidden = Math.max(0, items.length - visibleItems.length);
          return (
            <div
              key={s.key}
              className={`shrink-0 w-72 rounded-lg border transition-colors ${stageChipCls(s.color).split(" ").slice(1).join(" ")} ${isDragOver ? "ring-2 ring-teal-400/60 bg-teal-500/[0.04]" : ""}`}
              data-testid={`board-col-${s.key}`}
              onDragOver={(e) => onDragOver(e, s.key)}
              onDragLeave={() => setDragOver((cur) => (cur === s.key ? null : cur))}
              onDrop={(e) => onDrop(e, s.key)}
            >
              <div className="px-3 py-2.5 border-b border-white/5">
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${dotColor}`} />
                  <div className={`text-[12.5px] font-semibold truncate ${stageChipCls(s.color).split(" ")[0]}`}>{s.label}</div>
                </div>
                <div className="mt-1 flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-slate-500">
                  <span className="tabular-nums">{items.length} deal{items.length === 1 ? "" : "s"}</span>
                  <span>·</span>
                  <span>{GBP(total)}</span>
                  {s.probability > 0 && (<><span>·</span><span>{s.probability}%</span></>)}
                </div>
              </div>
              <div className="p-2 space-y-2 min-h-[120px] max-h-[calc(100vh-260px)] overflow-y-auto">
                {items.length === 0 && (
                  <div className="text-center text-[11px] text-slate-600 font-mono py-6 border border-dashed border-white/[0.06] rounded">
                    {isDragOver ? "Drop to move" : "empty"}
                  </div>
                )}
                {visibleItems.map((c) => (
                  <div
                    key={c.id}
                    draggable
                    onDragStart={(e) => onDragStart(e, c)}
                    data-testid={`board-card-drag-${c.id}`}
                  >
                    <BoardCard card={c} onNavigate={onNavigate} sla={sla} />
                  </div>
                ))}
                {hidden > 0 && (
                  <button
                    type="button"
                    onClick={() => bumpCol(s.key)}
                    className="w-full text-center text-[11px] font-mono uppercase tracking-widest text-slate-400 hover:text-white py-2 rounded border border-dashed border-white/10 hover:border-white/30 hover:bg-white/[0.03]"
                    data-testid={`board-col-more-${s.key}`}
                    title={`Show next ${Math.min(INITIAL_PER_COL, hidden)} of ${hidden}`}
                  >
                    Show {Math.min(INITIAL_PER_COL, hidden)} more · {hidden} hidden
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─── quotes-peek popover — shared with CustomerPicker, reused inline here ─── */
function PipelineQuotesPeek({ deal, onClose, onChanged }) {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    if (!deal?.id) return;
    setItems(null);
    try {
      const email = (deal.email || "").trim();
      if (!email) { setItems([]); return; }
      const p = new URLSearchParams({ scope: "all", search: email, limit: "25", sort: "updated", include_archived: showArchived ? "true" : "false" });
      const r = await axios.get(`${API}/quotes?${p.toString()}`, { withCredentials: true });
      setItems(r.data.items || []);
    } catch { setItems([]); }
  }, [deal, showArchived]);

  useEffect(() => { if (deal) load(); }, [deal, load]);

  if (!deal) return null;

  const doArchive = async (row) => {
    const archived = row.Status === "Archived";
    if (!archived && !window.confirm(`Archive quote ${row.QuoteRef || ""}? It will be hidden from active lists (restore any time).`)) return;
    setBusyId(row._id);
    try {
      await axios.post(`${API}/quotes/${row._id}/${archived ? "unarchive" : "archive"}`, archived ? {} : { reason: "Archived from Deal Pipeline quotes peek" }, { withCredentials: true });
      toast.success(archived ? "Quote restored" : "Quote archived");
      await load(); onChanged?.();
    } catch { toast.error("Action failed"); }
    finally { setBusyId(null); }
  };

  return createPortal(
    <div
      onClick={onClose}
      className="fixed inset-0 z-[110] flex items-center justify-center bg-black/70 backdrop-blur"
      data-testid="deal-quotes-peek-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="rounded-2xl overflow-hidden shadow-[0_30px_80px_rgba(0,0,0,0.7)]"
        style={{ width: "min(680px, 94vw)", maxHeight: "80vh", background: "#0F1A30", border: "1px solid rgba(47,200,203,0.28)" }}
        data-testid="deal-quotes-peek"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 gap-3">
          <div className="min-w-0">
            <div className="text-[10.5px] uppercase tracking-widest text-teal-300 font-mono font-bold">Quotes for</div>
            <div className="text-[15px] font-bold text-white truncate">{deal.full_name}</div>
            <div className="text-[11px] text-slate-400 font-mono truncate">{deal.email}</div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <label className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-slate-400 cursor-pointer">
              <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} data-testid="deal-peek-show-archived" />
              Archived
            </label>
            <button onClick={onClose} className="p-1.5 rounded hover:bg-white/5 text-slate-400" data-testid="deal-peek-close"><X className="w-4 h-4" /></button>
          </div>
        </div>
        <div className="p-2 overflow-y-auto" style={{ maxHeight: "calc(80vh - 68px)" }}>
          {items === null && <div className="p-6 text-center text-[11px] text-slate-500 font-mono">Loading…</div>}
          {items && items.length === 0 && (
            <div className="p-6 text-center text-[11px] text-slate-500 font-mono">No quotes for this customer.</div>
          )}
          {items && items.map((q) => (
            <div key={q._id} className="flex items-center gap-2 px-3 py-2 rounded border border-white/[0.06] bg-white/[0.02] mb-1.5" data-testid={`deal-peek-row-${q._id}`}>
              <span className="font-mono text-[12px] text-orange-300 min-w-[100px]">{q.QuoteRef}</span>
              <div className="flex-1 min-w-0">
                <div className="text-[12.5px] text-white truncate">{q.Status || "Draft"}</div>
                <div className="text-[10px] text-slate-500 font-mono truncate">{q.AccountManager || "—"} · {q.Totals?.total != null ? GBP_FULL(q.Totals.total) : "—"}</div>
              </div>
              <button
                onClick={() => { onClose(); navigate(`/quotes/${q._id}/edit`); }}
                className="px-2.5 py-1 rounded border border-teal-500/40 bg-teal-500/10 text-teal-200 hover:bg-teal-500 hover:text-black text-[10px] font-mono uppercase tracking-widest"
                data-testid={`deal-peek-open-${q._id}`}
              >Open</button>
              <button
                onClick={() => doArchive(q)}
                disabled={busyId === q._id}
                className={`px-2.5 py-1 rounded border text-[10px] font-mono uppercase tracking-widest ${q.Status === "Archived" ? "border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10" : "border-rose-500/40 text-rose-300 hover:bg-rose-500/10"}`}
                data-testid={`deal-peek-archive-${q._id}`}
              >{q.Status === "Archived" ? "Restore" : "Archive"}</button>
            </div>
          ))}
        </div>
      </div>
    </div>,
    document.body,
  );
}

/* ─── main page ─── */
export default function DealPipeline() {
  const can = useCan("/deals");
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme } = useTheme();
  const queryClient = useQueryClient();
  const managers = useAccountManagers();
  const [view, setView] = useState(() => localStorage.getItem("deals.view") || "board");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState({ items: [], stages: [], facets: {}, total: 0, total_ltv: 0 });

  // Filters (chip-style)
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [stages, setStages] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [rot, setRot] = useState([]);      // ["rotting"] or ["healthy"]
  const [ams, setAms] = useState([]);
  const [hasQuote, setHasQuote] = useState([]); // ["true"] or ["false"]
  const [build, setBuild] = useState([]);       // quote build types (multi)
  const [urgencyMin, setUrgencyMin] = useState(""); // "", "4","7","9"
  const [asapOnly, setAsapOnly] = useState(false);
  const [focus, setFocus] = useState("");       // overdue/due_soon/expiring/expired/stale
  const [dateRange, setDateRange] = useState(EMPTY_DATE_FILTER);
  const [sort, setSort] = useState("updated");
  // Per-page cap (bandwidth-conscious for offshore/low-bandwidth users).
  // Persist the pick so users don't have to re-pick every visit.
  const [pageSize, setPageSize] = useState(() => {
    const saved = Number(localStorage.getItem("deals.pageSize") || 100);
    return [25, 50, 100, 200, 500].includes(saved) ? saved : 100;
  });
  useEffect(() => { localStorage.setItem("deals.pageSize", String(pageSize)); }, [pageSize]);
  // "My deals" mode — defaults ON so an AM lands on their own book (and loads
  // ALL of it, not just one page). Explicit + visible, unlike the old silent filter.
  const [mine, setMine] = useState(() => localStorage.getItem("deals.mine") !== "0");
  useEffect(() => { localStorage.setItem("deals.mine", mine ? "1" : "0"); }, [mine]);
  // Infinite-scroll accumulation for the TABLE view (kanban always shows page 0).
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const offsetRef = useRef(0);
  const loadingMoreRef = useRef(false);
  const genRef = useRef(0);
  const [expandedIds, setExpandedIds] = useState({});

  // Snapshot of everything that describes the "current view" so users can save
  // it as a named preset via SavedViewChips. Includes filters + sort + view
  // mode so a saved "Gold · Rotting · board" round-trips cleanly.
  const viewSnapshot = useMemo(() => ({
    search: search.trim(),
    stages, tiers, ams, rot, hasQuote,
    build, urgencyMin, asapOnly, focus, dateRange,
    sort, view,
  }), [search, stages, tiers, ams, rot, hasQuote, build, urgencyMin, asapOnly, focus, dateRange, sort, view]);

  const savedViews = useSavedViews("deals.savedViews", viewSnapshot);

  const applySavedView = useCallback((snap) => {
    if (!snap) return;
    setSearch(snap.search || "");
    setSearchInput(snap.search || "");
    setStages(Array.isArray(snap.stages) ? snap.stages : []);
    setTiers(Array.isArray(snap.tiers) ? snap.tiers : []);
    setAms(Array.isArray(snap.ams) ? snap.ams : []);
    setRot(Array.isArray(snap.rot) ? snap.rot : []);
    setHasQuote(Array.isArray(snap.hasQuote) ? snap.hasQuote : []);
    setBuild(Array.isArray(snap.build) ? snap.build : (snap.build ? [snap.build] : []));
    setUrgencyMin(snap.urgencyMin || "");
    setAsapOnly(!!snap.asapOnly);
    setFocus(snap.focus || "");
    setDateRange(normalizeDateFilter(snap.dateRange));
    if (snap.sort) setSort(snap.sort);
    if (snap.view === "table" || snap.view === "board") setView(snap.view);
  }, []);

  const toggleExpand = (id) => setExpandedIds((m) => ({ ...m, [id]: !m[id] }));
  const newQuoteFor = (personId) => navigate(`/quotes/new?customer=${personId}`);

  // The logged-in user's Account-Manager name (if they are one). Drives "My deals".
  const myAmName = useMemo(() => {
    if (!user || !managers.length) return null;
    const me = managers.find(
      (m) => m.id === user.id
        || (user.email && m.email && m.email.toLowerCase() === user.email.toLowerCase())
        || m.name === user.name
    );
    return me ? me.name : null;
  }, [user, managers]);

  // Peek quotes modal state — mirrors CustomerPicker's popover but scoped per deal.
  const [peekDeal, setPeekDeal] = useState(null);
  const [peekQuoteId, setPeekQuoteId] = useState(null);

  // Customer 360 centre-modal overlay (Phase 3) — clicking a card/row opens the
  // profile in-place so board filters/scroll stay intact.
  const [person360Id, setPerson360Id] = useState(null);

  // Reopen the 360 overlay when returning from a full-profile page (return contract §5).
  const location = useLocation();
  useEffect(() => {
    if (location.state?.open360) setPerson360Id(location.state.open360);
  }, [location.state]);

  const changeStage = useCallback(async (personId, stageKey) => {
    try {
      await axios.patch(`${API}/deals/${personId}/stage`, { stage_key: stageKey }, { withCredentials: true });
      toast.success("Stage updated");
      load();
    } catch (err) {
      toast.error("Failed to change stage", { description: err?.response?.data?.detail || "" });
    }
     
  }, []);

  // ── Item 3: client-side header sort of the loaded table page ──
  const [sortKey, setSortKey] = useState(null);   // null = server/default order
  const [salesDays, setSalesDaysState] = useState(() => { const v = Number(localStorage.getItem("deals.salesDays")); return [7, 30, 90].includes(v) ? v : 30; });
  const setSalesDays = (d) => { setSalesDaysState(d); localStorage.setItem("deals.salesDays", String(d)); };
  const [sortDir, setSortDir] = useState("asc");
  // Cleaner 3-state cycle: asc → desc → off
  const onHeaderSort = useCallback((key) => {
    const first = ["ltv", "sales", "quotevalue", "open", "orders", "quotes", "urgency", "lastorder", "updated"].includes(key) ? "desc" : "asc";
    const second = first === "asc" ? "desc" : "asc";
    if (sortKey !== key) { setSortKey(key); setSortDir(first); return; }
    if (sortDir === first) { setSortDir(second); return; }
    setSortKey(null); setSortDir("asc");
     
  }, [sortKey, sortDir]);

  const sortedItems = useMemo(() => {
    const items = data.items || [];
    if (!sortKey) return items;
    const stageOrder = Object.fromEntries((data.stages || []).map((s, i) => [s.key, i]));
    const val = (d) => {
      switch (sortKey) {
        case "name":     return (d.full_name || d.company || "").toLowerCase();
        case "tier":     return (d.tier || "").toLowerCase();
        case "stage":    return stageOrder[d.stage_key] ?? 999;
        case "ltv":      return Number(d.ltv || 0);
        case "sales":    return Number(d[`sales_${salesDays}`] || 0);
        case "orders":   return Number(d.orders_count || 0);
        case "quotes":   return Number(d.quote_count || 0);
        case "open":     return Number(d.open_value || 0);
        case "quotevalue": return Number(d.latest_quote_value || 0);
        case "am":       return (d.am || "").toLowerCase();
        case "urgency":  return Number(d.urgency || 0);
        case "target":   return new Date(d.target_date || 8640000000000000).getTime();
        case "location": return (d.post_code || d.location || d.city || d.postcode || "").toLowerCase();
        case "lastorder": return new Date(d.last_order_date || 0).getTime();
        case "updated":  return new Date(d.last_updated || d.updated_at || d.last_activity_at || 0).getTime();
        default:         return 0;
      }
    };
    const dir = sortDir === "asc" ? 1 : -1;
    return [...items].sort((a, b) => {
      const va = val(a), vb = val(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
  }, [data.items, data.stages, sortKey, sortDir, salesDays]);

  // ── Table row virtualization (window scroll) — only on-screen rows stay
  //    mounted; expandable rows are dynamically measured so heights stay correct.
  const dealsTableRef = useRef(null);
  const [dealsTableOffset, setDealsTableOffset] = useState(0);
  useLayoutEffect(() => {
    const measure = () => setDealsTableOffset(dealsTableRef.current?.offsetTop ?? 0);
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [view]);
  const dealVirtualizer = useWindowVirtualizer({
    count: sortedItems.length,
    estimateSize: () => 60,
    overscan: 8,
    getItemKey: (i) => sortedItems[i]?.id ?? i,
    scrollMargin: dealsTableOffset,
  });
  const dealVItems = dealVirtualizer.getVirtualItems();
  const dealPadTop = dealVItems.length > 0 ? Math.max(0, dealVItems[0].start - dealVirtualizer.options.scrollMargin) : 0;
  const dealPadBottom = dealVItems.length > 0 ? Math.max(0, dealVirtualizer.getTotalSize() - dealVItems[dealVItems.length - 1].end) : 0;
  const activityIds = useMemo(() => sortedItems.map((d) => d.id), [sortedItems]);
  const { summaries: activitySummaries, refresh: refreshActivity } = useActivitySummaries(activityIds);

  // ── Item 5: bulk stage-assign (table multi-select) ──
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkActivityOpen, setBulkActivityOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const toggleSelect = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);
  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);
  // Clear selection on filter/view resets — but NOT when scrolling appends rows.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { clearSelection(); }, [search, stages, tiers, ams, rot, hasQuote, sort, pageSize, view]);
  const bulkAssignStage = useCallback(async (stageKey) => {
    if (!stageKey || selectedIds.size === 0) return;
    setBulkBusy(true);
    const ids = [...selectedIds];
    try {
      const results = await Promise.allSettled(
        ids.map((id) => axios.patch(`${API}/deals/${id}/stage`, { stage_key: stageKey }, { withCredentials: true }))
      );
      const ok = results.filter((r) => r.status === "fulfilled").length;
      const failed = results.length - ok;
      if (ok) toast.success(`Moved ${ok} deal${ok === 1 ? "" : "s"}`);
      if (failed) toast.error(`${failed} failed to move`);
      clearSelection();
      load();
    } finally { setBulkBusy(false); }
     
  }, [selectedIds, clearSelection]);

  // Bulk AM assign/clear — managers and above, or AM Management access.
  const _dpIsAdmin = useCanEditAm();
  const [amUsers, setAmUsers] = useState([]);
  useEffect(() => {
    if (!_dpIsAdmin) return;
    axios.get(`${API}/admin/am-management/users`, { withCredentials: true })
      .then(({ data }) => setAmUsers(data.items || [])).catch(() => {});
  }, [_dpIsAdmin]);
  const bulkAssignAm = useCallback(async (userId, clear) => {
    if (selectedIds.size === 0) return;
    setBulkBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/am-management/bulk-set-am`,
        clear ? { person_ids: [...selectedIds], clear: true } : { person_ids: [...selectedIds], userId },
        { withCredentials: true });
      toast.success(clear ? `Cleared AM on ${data.cleared} customer(s)` : `Assigned ${data.am} to ${data.assigned}`);
      clearSelection(); load();
    } catch (e) { toast.error(String(e?.response?.data?.detail || "Bulk AM update failed")); }
    finally { setBulkBusy(false); }
  }, [selectedIds, clearSelection]);

  // ── Board horizontal scroll — nudge left/right (arrows replace the old
  //    stage-jump chip strip; native scrollbar remains at the bottom).
  const boardRef = useRef(null);
  const [boardHover, setBoardHover] = useState(false);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);
  const updateEdges = useCallback(() => {
    const el = boardRef.current;
    if (!el) return;
    setAtStart(el.scrollLeft <= 2);
    setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 2);
  }, []);
  const scrollBoard = useCallback((dir) => {
    const el = boardRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.round(el.clientWidth * 0.8), behavior: "smooth" });
    setTimeout(updateEdges, 450);
  }, [updateEdges]);

  // ── SLA config (warm-reply clock on AI CX Replied) — off unless enabled ──
  const [slaConfig, setSlaConfig] = useState(null);
  useEffect(() => {
    axios.get(`${API}/config/pipeline-automation`, { withCredentials: true })
      .then((r) => setSlaConfig(r.data?.sla || null))
      .catch(() => {});
  }, []);

  // Orphan quotes = quotes with no Customer.email attached.
  const [orphans, setOrphans] = useState({ count: 0, items: [] });
  const loadOrphans = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/deals/orphan-quotes?limit=5`, { withCredentials: true });
      setOrphans(r.data);
    } catch (_) { /* silent */ }
  }, []);
  useEffect(() => { loadOrphans(); }, [loadOrphans]);

  useEffect(() => { localStorage.setItem("deals.view", view); }, [view]);

  const buildParams = useCallback((offset) => {
    const effMine = mine && myAmName;
    const params = new URLSearchParams({ limit: view === "board" ? 500 : pageSize, offset, sort });
    if (search.trim()) params.set("search", search.trim());
    if (stages.length) params.set("stage", stages.join(","));
    if (tiers.length) params.set("tier", tiers.join(","));
    if (effMine) params.set("am", myAmName);
    else if (ams.length) params.set("am", ams.join(","));
    if (rot.length === 1) params.set("rot", rot[0]);
    if (hasQuote.length === 1) params.set("has_quote", hasQuote[0]);
    if (build.length) params.set("build", build.join(","));
    if (urgencyMin) params.set("urgency", urgencyMin);
    if (asapOnly) params.set("asap", "true");
    if (focus) params.set("focus", focus);
    applyDateFilterParams(params, dateRange);
    return params;
  }, [search, stages, tiers, ams, rot, hasQuote, build, urgencyMin, asapOnly, focus, dateRange, sort, pageSize, mine, myAmName, view]);

  const freshRef = useRef(false);
  const load = useCallback(async () => {
    genRef.current += 1;
    const gen = genRef.current;
    offsetRef.current = 0;
    loadingMoreRef.current = false;
    setLoadingMore(false);
    setLoading(true); setError(null); setHasMore(true);
    try {
      const p0 = buildParams(0);
      if (freshRef.current) { p0.set("fresh", "1"); freshRef.current = false; }
      const url = `${API}/deals?${p0.toString()}`;
      const rdata = await queryClient.fetchQuery({
        queryKey: ["deals", p0.toString()],
        queryFn: () => axios.get(url, { withCredentials: true }).then((res) => res.data),
        staleTime: 0,
        gcTime: 300000,
      });
      if (gen !== genRef.current) return;
      const items = rdata.items || [];
      setData(rdata);
      offsetRef.current = items.length;
      setHasMore(items.length < (rdata.total_matching ?? rdata.total ?? items.length));
    } catch (e) {
      if (gen === genRef.current && !isCancelledError(e)) setError(e.response?.data?.detail || "Failed to load deals");
    } finally {
      if (gen === genRef.current) setLoading(false);
    }
  }, [buildParams, queryClient]);

  // Table-view only: append the next batch as the sentinel scrolls into view.
  const loadMore = useCallback(async () => {
    if (loadingMoreRef.current || !hasMore || loading) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    const gen = genRef.current;
    try {
      const p = buildParams(offsetRef.current);
      p.set("limit", "500"); // rows are cached server-side, so stream in big batches
      const url = `${API}/deals?${p.toString()}`;
      const rdata = await queryClient.fetchQuery({
        queryKey: ["deals", p.toString()],
        queryFn: () => axios.get(url, { withCredentials: true }).then((res) => res.data),
        staleTime: 120000,
        gcTime: 300000,
      });
      if (gen !== genRef.current) return;
      const batch = rdata.items || [];
      setData((prev) => {
        const seen = new Set((prev.items || []).map((it) => it.id));
        const merged = [...(prev.items || []), ...batch.filter((it) => !seen.has(it.id))];
        return { ...prev, ...rdata, items: merged };
      });
      offsetRef.current += batch.length;
      setHasMore(offsetRef.current < (rdata.total_matching ?? rdata.total ?? offsetRef.current));
    } catch (e) {
      if (!isCancelledError(e) && gen === genRef.current) setHasMore(false);
    } finally {
      if (gen === genRef.current) { loadingMoreRef.current = false; setLoadingMore(false); }
    }
  }, [buildParams, hasMore, loading, queryClient]);

  // Background buffered pagination: once the first page lands, keep streaming
  // the remaining pages automatically (in pageSize batches) until the FULL set
  // is loaded — no manual "load more", and the board view fills completely.
  // Safety-capped so a huge dataset can't buffer forever.
  useEffect(() => {
    if (loading || loadingMore || !hasMore) return;
    if ((data.items?.length || 0) >= 5000) return;
    const t = setTimeout(() => loadMore(), 100);
    return () => clearTimeout(t);
  }, [loading, loadingMore, hasMore, data.items?.length, loadMore]);

  useEffect(() => { load(); }, [load]);

  // Search button / Enter — commit the typed text and force a fresh server-wide fetch (no cached pages).
  const runSearch = useCallback(async () => {
    genRef.current += 1;
    await queryClient.cancelQueries({ queryKey: ["deals"] });
    queryClient.removeQueries({ queryKey: ["deals"] });
    freshRef.current = true;
    if (searchInput.trim() !== search.trim()) setSearch(searchInput);
    else load();
  }, [queryClient, searchInput, search, load]);

  const sel = "dark-select h-9 border border-white/10 rounded-md px-2 text-[12px] outline-none focus:border-orange-400/60";
  const tierOpts = TIER_OPTS.map((t) => ({ value: t, label: `${t}${data.facets?.tiers?.[t] ? ` (${data.facets.tiers[t]})` : ""}` }));
  const amOpts = managers.map((m) => ({ value: m.email, label: m.name }));
  const stageOpts = (data.stages || []).map((s) => ({ value: s.key, label: `${s.label}${data.facets?.stages?.[s.key] ? ` (${data.facets.stages[s.key]})` : ""}` }));
  const buildOpts = Object.keys(data.facets?.builds || {}).sort().map((b) => ({ value: b, label: `${b} (${data.facets.builds[b]})` }));

  const activeFilterCount = stages.length + tiers.length + ams.length + rot.length + hasQuote.length + (search ? 1 : 0) + build.length + (urgencyMin ? 1 : 0) + (asapOnly ? 1 : 0) + (focus ? 1 : 0) + (isDateFilterActive(dateRange) ? 1 : 0);
  const clearAll = () => { setSearch(""); setSearchInput(""); setStages([]); setTiers([]); setAms([]); setRot([]); setHasQuote([]); setBuild([]); setUrgencyMin(""); setAsapOnly(false); setFocus(""); setDateRange(EMPTY_DATE_FILTER); };

  const amOptions = useMemo(
    () => Object.keys(data.facets?.assignees || {}).sort(),
    [data.facets],
  );

  return (
    <QuotePeekCtx.Provider value={setPeekQuoteId}>
    <NotesPeekProvider onOpen360={setPerson360Id}>
    <Layout mainProps={theme === "light" ? { "data-talmia-theme": "light" } : {}}>
      <div className="space-y-5" data-testid="deal-pipeline-page">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-5 flex-wrap">
           <div>
            <div className="text-[10px] uppercase tracking-widest text-orange-400 font-mono mb-1 flex items-center gap-1.5">
              <GitBranch className="w-3 h-3" /> Deal Pipeline
            </div>
            <h1 className="text-3xl font-display font-bold tracking-tight flex items-center gap-3">
              Deals
              <LiveCount shown={data.items?.length || 0} total={data.total_matching || data.total || 0} noun="deal" testId="deals-live-count" />
            </h1>
            <p className="text-slate-400 text-sm mt-1">
              <span className="text-white font-semibold">{data.total_matching ?? data.total}</span> deal{(data.total_matching ?? data.total) === 1 ? "" : "s"}
              {" · "}
              <span className="text-emerald-300 font-semibold tabular-nums">{GBP(data.stats?.totals?.ltv ?? data.total_ltv)}</span> combined LTV
              {" · "}
              <span className="text-lime-300 font-semibold tabular-nums" data-testid="deals-period-sales-total">{GBP(data.stats?.totals?.[`sales_${salesDays}`] ?? 0)}</span> sales last {salesDays}d
              <span className="text-slate-600 ml-2 text-[11px] font-mono">unified · one deal per customer</span>
            </p>
           </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-lg border border-white/10 overflow-hidden">
              <button
                type="button"
                onClick={() => setView("table")}
                data-testid="view-table"
                className={`px-3 py-1.5 text-[11px] font-mono uppercase tracking-widest inline-flex items-center gap-1.5 ${
                  view === "table" ? "bg-orange-500/15 text-orange-200" : "text-slate-400 hover:text-white"
                }`}
              >
                <TableIcon className="w-3 h-3" /> Table
              </button>
              <button
                type="button"
                onClick={() => setView("board")}
                data-testid="view-board"
                className={`px-3 py-1.5 text-[11px] font-mono uppercase tracking-widest inline-flex items-center gap-1.5 ${
                  view === "board" ? "bg-orange-500/15 text-orange-200" : "text-slate-400 hover:text-white"
                }`}
              >
                <LayoutGrid className="w-3 h-3" /> Board
              </button>
            </div>
            <button
              type="button"
              onClick={() => { freshRef.current = true; load(); }}
              disabled={loading}
              data-testid="deals-refresh"
              className="w-9 h-9 grid place-items-center rounded-lg border border-white/10 bg-white/[0.03] text-slate-300 hover:text-white hover:bg-white/[0.06] disabled:opacity-50"
              aria-label="Refresh"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            </button>
            {can("edit") && <button
              type="button"
              onClick={() => navigate("/quotes/new")}
              data-testid="deals-new-quote"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-orange-500/15 border border-orange-500/40 text-orange-200 text-sm hover:bg-orange-500/25"
            >
              <Plus className="w-3.5 h-3.5" /> New quote
            </button>}
          </div>
        </div>

        {/* Orphan-quotes banner */}
        {orphans.count > 0 && (
          <div
            className="rounded-lg border border-amber-500/30 bg-amber-500/[0.06] px-3 py-2 flex items-center gap-3 flex-wrap"
            data-testid="deals-orphan-banner"
          >
            <AlertTriangle className="w-4 h-4 text-amber-300 shrink-0" />
            <div className="text-[12px] text-amber-100 flex-1 min-w-[240px]">
              <span className="font-semibold">{orphans.count}</span> quote{orphans.count === 1 ? "" : "s"} with no customer attached
              <span className="text-amber-300/60"> — they don&rsquo;t appear in any deal.</span>
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              {orphans.items.slice(0, 3).map((q) => (
                <Link
                  key={q.id}
                  to={`/quotes/${q.id}/edit`}
                  data-testid={`orphan-quote-${q.quote_ref || q.id}`}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded border border-amber-500/30 bg-amber-500/[0.08] text-amber-100 text-[11px] font-mono hover:bg-amber-500/[0.16]"
                >
                  <FileText className="w-3 h-3" /> {q.quote_ref || q.id.slice(-6)}
                  <span className="text-amber-300/70">· {q.status}</span>
                </Link>
              ))}
              <Link
                to="/deals/orphan-quotes"
                data-testid="orphan-manage-btn"
                className="inline-flex items-center gap-1 px-2 py-1 rounded border border-amber-500/50 bg-amber-500/[0.14] text-amber-100 text-[11px] font-mono hover:bg-amber-500/[0.22]"
              >
                Manage all →
              </Link>
            </div>
          </div>
        )}

        {/* Filter bar — mirrors View Quotes: search + Search button + sort, then multi-select dropdowns */}
        <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3 space-y-2.5" data-testid="deals-filters">
          <form
            className="flex items-center gap-2 flex-wrap"
            onSubmit={(e) => { e.preventDefault(); runSearch(); }}
            data-testid="deals-search-form"
          >
            <div className="relative flex-1 min-w-[220px]">
              <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search name, company, email, phone, postcode, quote ref, project, AM…"
                data-testid="deals-search"
                className="w-full h-9 pl-10 pr-8 rounded-lg bg-white/[0.04] border border-white/10 focus:border-orange-400/60 outline-none text-sm text-white placeholder:text-slate-500"
              />
              {searchInput && (
                <button type="button" onClick={() => { setSearchInput(""); setSearch(""); }} data-testid="deals-search-clear"
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-500 hover:text-white" aria-label="Clear search">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="deals-search-btn"
              title="Search every deal on the server with the current filters"
              className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-orange-500/20 border border-orange-400/50 text-orange-100 text-[12px] font-semibold hover:bg-orange-500/30 disabled:opacity-50 transition-colors"
            >
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />} Search all
            </button>
            <select
              value={sort}
              onChange={(e) => { setSort(e.target.value); setSortKey(null); }}
              data-testid="deals-sort"
              className={sel}
              title="Sorts every matching deal on the server"
            >
              <option value="updated">Sort: Last updated</option>
              <option value="name">Sort: Name (A–Z)</option>
              <option value="ltv">Sort: LTV (high → low)</option>
              <option value={`sales_${salesDays}`}>{`Sort: Period sales ${salesDays}d (high → low)`}</option>
              <option value="quote_value">Sort: Quote value (high → low)</option>
              <option value="open_value">Sort: Open £ (high → low)</option>
              <option value="orders">Sort: Orders (high → low)</option>
              <option value="last_order">Sort: Last order (newest)</option>
              <option value="urgency">Sort: Urgency (high → low)</option>
              <option value="target">Sort: Target date (soonest)</option>
              <option value="rot">Sort: Rot (most overdue)</option>
            </select>
            {view === "table" && <select
              onChange={(e) => setPageSize(Number(e.target.value))}
              data-testid="deals-page-size"
              className={sel}
              title="Rows per fetch — lower = less bandwidth"
            >
              {[25, 50, 100, 200, 500].map((n) => <option key={n} value={n}>{n} / page</option>)}
            </select>}
            <span className="text-[11px] text-slate-500 font-mono" data-testid="deals-count-summary">
              {data.total ? `${data.items?.length || 0} of ${data.total_matching || data.total}` : ""}
            </span>
          </form>

          {/* Compact filter row — dropdowns keep height tight while adding the
              quote-driven signals (Build / Urgency / ASAP / Focus) alongside the
              core filters. Stage chips retired (stages are board columns + sortable header). */}
          <div className="flex items-center gap-2 flex-wrap text-[12px]">
            {myAmName && (
              <button
                type="button"
                onClick={() => setMine((v) => !v)}
                data-testid="deals-mine-toggle"
                title={mine ? "Showing only YOUR deals (your whole book). Click to see everyone's." : "Show only your deals"}
                className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-md border font-mono uppercase tracking-wider text-[11px] font-semibold transition-colors ${
                  mine
                    ? "bg-emerald-500/20 text-emerald-200 border-emerald-400/60 mine-active"
                    : "bg-transparent text-slate-400 border-white/15 hover:text-white hover:border-white/30"
                }`}
              >
                <UserCheck className="w-3.5 h-3.5" /> My Deals
              </button>
            )}
            <MultiSelectDropdown label="Stage" testid="deals-stage" options={stageOpts} selected={stages} onChange={setStages} width="min-w-[110px]" />
            <MultiSelectDropdown label="Tier" testid="deals-tier" options={tierOpts} selected={tiers} onChange={setTiers} width="min-w-[100px]" />
            {managers.length > 0 && !(mine && myAmName) && (
              <MultiSelectDropdown label="AM" testid="deals-am" options={amOpts} selected={ams} onChange={setAms} width="min-w-[100px]" />
            )}
            <DateRangeFilter value={dateRange} onChange={setDateRange} fields={DEAL_DATE_FIELDS} testid="deals-date-filter" />

            <span className="w-px h-6 bg-white/10 mx-0.5" />
            <MultiSelectDropdown label="Build" testid="deals-build" options={buildOpts} selected={build} onChange={setBuild} width="min-w-[100px]" />
            <select value={urgencyMin} onChange={(e) => setUrgencyMin(e.target.value)} data-testid="deals-filter-urgency" className={`${sel}${urgencyMin ? " filter-active" : ""}`}>
              <option value="">Urgency: any</option>
              <option value="4">Warm 4+</option>
              <option value="7">Hot 7+</option>
              <option value="9">Critical 9+</option>
            </select>
            <select value={focus} onChange={(e) => setFocus(e.target.value)} data-testid="deals-filter-focus" className={`${sel}${focus ? " filter-active" : ""}`} title="Timeline & chase buckets from the master quote">
              <option value="">Focus: any</option>
              <option value="overdue">Overdue target</option>
              <option value="due_soon">Due ≤7 days</option>
              <option value="expiring">Expiring ≤7 days</option>
              <option value="expired">Expired</option>
              <option value="stale">Stale — chase (30d+)</option>
            </select>
            <button
              type="button"
              onClick={() => setAsapOnly((v) => !v)}
              data-testid="deals-filter-asap"
              className={`inline-flex items-center gap-1 h-9 px-2.5 rounded-md border transition-colors ${asapOnly ? "bg-rose-500/20 text-rose-200 border-rose-500/40" : "border-white/10 text-slate-400 hover:text-white"}`}
              title="Show only deals with an ASAP open quote"
            >
              <Zap className="w-3.5 h-3.5" /> ASAP
            </button>

            <span className="w-px h-6 bg-white/10 mx-0.5" />
            <select value={rot[0] || ""} onChange={(e) => setRot(e.target.value ? [e.target.value] : [])} data-testid="deals-filter-rot" className={`${sel}${rot.length ? " filter-active" : ""}`}>
              <option value="">Rot: any</option>
              <option value="rotting">Rotting</option>
              <option value="healthy">Healthy</option>
            </select>
            <select value={hasQuote[0] || ""} onChange={(e) => setHasQuote(e.target.value ? [e.target.value] : [])} data-testid="deals-filter-hasquote" className={`${sel}${hasQuote.length ? " filter-active" : ""}`}>
              <option value="">Quote: any</option>
              <option value="true">Has open quote</option>
              <option value="false">No quote</option>
            </select>
          </div>

          {activeFilterCount > 0 && (
            <div className="flex items-center gap-1.5 flex-wrap pt-1 border-t border-white/[0.06]" data-testid="deals-active-summary">
              <span className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">{activeFilterCount} active</span>
              <button type="button" onClick={clearAll} data-testid="deals-clear-filters"
                className="text-[11px] font-mono text-orange-300 hover:text-orange-100 underline underline-offset-2">Clear all</button>
            </div>
          )}

          {/* Saved views — one-click filter presets, localStorage-backed. */}
          <div className="pt-1 border-t border-white/[0.04]">
            <SavedViewChips
              views={savedViews.views}
              currentSnapshot={viewSnapshot}
              onApply={applySavedView}
              onDelete={savedViews.remove}
              onSave={savedViews.saveCurrent}
              testidPrefix="deal-view"
            />
          </div>
        </div>

        <DealStatsStrip stats={data.stats} stages={data.stages || []} periodLabel={isDateFilterActive(dateRange) ? dateFilterLabel(dateRange) : null} />

        {/* Bulk stage-assign bar (Item 5) — table multi-select */}
        {view === "table" && selectedIds.size > 0 && can("manage") && (
          <div className="rounded-lg border border-teal-500/30 bg-teal-500/[0.06] px-3 py-2 flex items-center gap-3 flex-wrap" data-testid="deals-bulk-bar">
            <span className="inline-flex items-center gap-2 text-[12px] text-teal-100 font-medium">
              <Layers className="w-4 h-4 text-teal-300" />
              {selectedIds.size} selected
            </span>
            <div className="flex items-center gap-2 ml-auto flex-wrap">
              <label className="text-[10px] font-mono uppercase tracking-widest text-slate-400">Move to</label>
              <select
                defaultValue=""
                disabled={bulkBusy}
                onChange={(e) => { const v = e.target.value; e.target.value = ""; bulkAssignStage(v); }}
                data-testid="deals-bulk-stage"
                className="dark-select border border-white/10 rounded-md text-[12px] px-2 py-1.5 outline-none focus:border-teal-400/60 disabled:opacity-50"
              >
                <option value="" disabled>Select stage…</option>
                {data.stages.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
              {_dpIsAdmin && (
                <>
                  <label className="text-[10px] font-mono uppercase tracking-widest text-slate-400 ml-1">AM</label>
                  <select
                    defaultValue=""
                    disabled={bulkBusy}
                    onChange={(e) => { const v = e.target.value; e.target.value = ""; if (v) bulkAssignAm(v, false); }}
                    data-testid="deals-bulk-am"
                    className="dark-select border border-white/10 rounded-md text-[12px] px-2 py-1.5 outline-none focus:border-teal-400/60 disabled:opacity-50"
                  >
                    <option value="" disabled>Assign AM…</option>
                    {amUsers.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                  <button
                    type="button"
                    onClick={() => bulkAssignAm(null, true)}
                    disabled={bulkBusy}
                    data-testid="deals-bulk-am-clear"
                    className="text-[11px] font-mono px-2 py-1.5 rounded-md border border-rose-500/40 bg-rose-500/10 text-rose-200 hover:bg-rose-500/20 disabled:opacity-50"
                  >
                    Clear AM
                  </button>
                </>
              )}
              {bulkBusy && <Loader2 className="w-4 h-4 animate-spin text-teal-300" />}
              <button
                type="button"
                onClick={() => setBulkActivityOpen(true)}
                data-testid="deals-bulk-log-activity"
                className="text-[11px] font-mono px-2 py-1.5 rounded-md border border-purple-500/40 bg-purple-500/10 text-purple-200 hover:bg-purple-500/20 inline-flex items-center gap-1"
              >
                <Activity className="w-3 h-3" /> Log activity
              </button>
              <button
                type="button"
                onClick={clearSelection}
                data-testid="deals-bulk-clear"
                className="text-[11px] font-mono text-slate-400 hover:text-white inline-flex items-center gap-1"
              >
                <X className="w-3 h-3" /> Clear
              </button>
            </div>
          </div>
        )}

        {error && (
          <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 text-rose-200 text-sm px-3 py-2" data-testid="deals-error">
            {error}
          </div>
        )}

        {/* Body */}
        <BufferProgressBar
          loaded={data.items?.length || 0}
          total={data.total_matching ?? data.total ?? 0}
          active={(hasMore || loadingMore) && (data.items?.length || 0) > 0}
        />
        {loading && !data.items.length ? (
          <div className="p-12 text-center text-slate-500" data-testid="deals-loading">Loading deals…</div>
        ) : view === "table" ? (
          <div className="rounded-lg border border-white/10 overflow-hidden" data-testid="deals-table-wrap">
            <div className="overflow-x-auto" ref={dealsTableRef}>
              <table className="w-full text-sm" data-testid="deals-table">
                <thead className="bg-orange-500/10 border-b border-orange-500/20">
                  <tr className="text-[10px] font-mono uppercase tracking-widest text-orange-200">
                    <th className="py-2 pl-3 pr-1 w-6">
                      <button
                        type="button"
                        onClick={() => {
                          const pageIds = sortedItems.map((d) => d.id);
                          const allSel = pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id));
                          setSelectedIds(allSel ? new Set() : new Set(pageIds));
                        }}
                        className="w-5 h-5 grid place-items-center rounded text-orange-200/70 hover:text-teal-300"
                        data-testid="deals-select-all"
                        aria-label="Select all on page"
                      >
                        {sortedItems.length > 0 && sortedItems.every((d) => selectedIds.has(d.id))
                          ? <CheckSquare className="w-4 h-4 text-teal-300" /> : <Square className="w-4 h-4" />}
                      </button>
                    </th>
                    <th className="py-2 pl-1 pr-2 w-8"></th>
                    <SortTh label="Deal · Customer" sortKey="name" activeKey={sortKey} dir={sortDir} onSort={onHeaderSort} />
                    <SortTh label="Tier" sortKey="tier" activeKey={sortKey} dir={sortDir} onSort={onHeaderSort} />
                    <SortTh label="Stage" sortKey="stage" activeKey={sortKey} dir={sortDir} onSort={onHeaderSort} />
                    <SortTh label="LTV" sortKey="ltv" activeKey={sortKey} dir={sortDir} onSort={onHeaderSort} align="right" />
                    <th className="py-2 pr-3 text-right select-none" data-testid="deals-col-period-sales">
                      <div className="inline-flex flex-col items-end gap-1">
                        <button type="button" onClick={() => onHeaderSort("sales")} data-testid="deals-th-sales" title="Actual paid/shipped sales inc VAT — separate from LTV"
                          className={`inline-flex items-center gap-1 font-mono uppercase tracking-widest hover:text-white ${sortKey === "sales" ? "text-orange-100" : "text-orange-200/80"}`}>
                          {sortKey === "sales" ? (sortDir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />) : <ArrowUpDown className="w-3 h-3 opacity-30" />}Period Sales
                        </button>
                        <span className="inline-flex rounded border border-white/10 overflow-hidden" data-testid="deals-sales-period">
                          {[7, 30, 90].map((d) => (
                            <button key={d} type="button" onClick={() => setSalesDays(d)} data-testid={`deals-sales-period-${d}`}
                              className={`px-1.5 py-0.5 text-[9px] font-mono ${salesDays === d ? "bg-lime-500/20 text-lime-200" : "text-slate-500 hover:text-white"}`}>{d}d</button>
                          ))}
                        </span>
                      </div>
                    </th>
                    <SortTh label="Quote £" sortKey="quotevalue" activeKey={sortKey} dir={sortDir} onSort={onHeaderSort} align="right" />
                    <SortTh label="Orders" sortKey="orders" activeKey={sortKey} dir={sortDir} onSort={onHeaderSort} align="right" />
                    <SortTh label="Quotes" sortKey="quotes" activeKey={sortKey} dir={sortDir} onSort={onHeaderSort} align="right" />
                    <SortTh label="Open £" sortKey="open" activeKey={sortKey} dir={sortDir} onSort={onHeaderSort} align="right" />
                    <SortTh label="Account Manager" sortKey="am" activeKey={sortKey} dir={sortDir} onSort={onHeaderSort} />
                    <th className="py-2 pr-3 text-left" data-testid="deals-col-activity">Activity</th>
                    <SortTh label="Location (postcode)" sortKey="location" activeKey={sortKey} dir={sortDir} onSort={onHeaderSort} />
                    <SortTh label="Last Order" sortKey="lastorder" activeKey={sortKey} dir={sortDir} onSort={onHeaderSort} />
                    <SortTh label="Updated" sortKey="updated" activeKey={sortKey} dir={sortDir} onSort={onHeaderSort} />
                  </tr>
                </thead>
                {data.items.length === 0 && (
                  <tbody>
                    <tr>
                      <td colSpan={16} className="text-center py-16 text-slate-500 text-sm">
                        No deals match the current filters.
                      </td>
                    </tr>
                  </tbody>
                )}
                {dealPadTop > 0 && (
                  <tbody aria-hidden="true"><tr><td colSpan={16} style={{ height: dealPadTop, padding: 0, border: 0 }} /></tr></tbody>
                )}
                {dealVItems.map((vr) => {
                  const d = sortedItems[vr.index];
                  return (
                    <DealRow
                      key={d.id}
                      deal={d}
                      stages={data.stages}
                      expanded={!!expandedIds[d.id]}
                      onToggleExpand={toggleExpand}
                      onNavigate={(id) => setPerson360Id(id)}
                      onNewQuote={newQuoteFor}
                      onQuoteArchived={() => load()}
                      onOwnerChanged={() => load()}
                      salesDays={salesDays}
                      onStageChange={changeStage}
                      onPeekQuotes={setPeekDeal}
                      selected={selectedIds.has(d.id)}
                      onToggleSelect={toggleSelect}
                      innerRef={dealVirtualizer.measureElement}
                      dataIndex={vr.index}
                      activitySummary={activitySummaries[d.id]}
                      onActivitySaved={refreshActivity}
                    />
                  );
                })}
                {dealPadBottom > 0 && (
                  <tbody aria-hidden="true"><tr><td colSpan={16} style={{ height: dealPadBottom, padding: 0, border: 0 }} /></tr></tbody>
                )}
              </table>
            </div>
            {(data.items?.length || 0) > 0 && (
              <InfiniteSentinel
                onReach={loadMore}
                hasMore={hasMore}
                loading={loadingMore}
                count={data.items?.length || 0}
                total={data.total_matching || data.total || 0}
                testId="deals-sentinel"
              />
            )}
          </div>
        ) : (
          <div
            className="space-y-2"
            onMouseEnter={() => { setBoardHover(true); updateEdges(); }}
            onMouseLeave={() => setBoardHover(false)}
            onScrollCapture={updateEdges}
          >
            {/* Board scroll arrows — fade in on hover; disabled at the ends */}
            <div className="flex items-center justify-between gap-2" data-testid="board-scroll-controls">
              <span className="text-[10.5px] text-slate-500 font-mono uppercase tracking-widest">
                {data.stages.length} stages · {data.items?.length || 0} deals
              </span>
              <div className={`flex items-center gap-1.5 transition-opacity duration-200 ${boardHover ? "opacity-100" : "opacity-0"}`}>
                <button
                  type="button"
                  onClick={() => scrollBoard(-1)}
                  disabled={atStart}
                  data-testid="board-scroll-left"
                  aria-label="Scroll board left"
                  className="w-8 h-8 grid place-items-center rounded-lg border border-white/10 text-slate-400 hover:text-white hover:border-white/25 hover:bg-white/[0.04] transition-colors disabled:opacity-30 disabled:cursor-default disabled:hover:bg-transparent disabled:hover:text-slate-400"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => scrollBoard(1)}
                  disabled={atEnd}
                  data-testid="board-scroll-right"
                  aria-label="Scroll board right"
                  className="w-8 h-8 grid place-items-center rounded-lg border border-white/10 text-slate-400 hover:text-white hover:border-white/25 hover:bg-white/[0.04] transition-colors disabled:opacity-30 disabled:cursor-default disabled:hover:bg-transparent disabled:hover:text-slate-400"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
            <BoardView
              deals={data.items}
              stages={data.stages}
              onNavigate={(id) => setPerson360Id(id)}
              onStageChange={changeStage}
              containerRef={boardRef}
              sla={slaConfig}
            />
            {hasMore && (
              <div className="flex justify-center py-3">
                <button
                  type="button"
                  onClick={loadMore}
                  disabled={loadingMore}
                  data-testid="board-load-more"
                  className="px-5 py-2 rounded-lg border border-orange-500/40 bg-orange-500/10 text-orange-200 text-[13px] font-medium hover:bg-orange-500/20 disabled:opacity-50"
                >
                  {loadingMore ? "Loading…" : `Load more deals (${data.items?.length || 0} of ${data.total_matching || data.total || 0})`}
                </button>
              </div>
            )}
          </div>
        )}

        <div className="text-[10.5px] text-slate-600 font-mono flex items-center gap-1.5">
          <History className="w-3 h-3" />
          One deal per customer · quotes nest inside · stages sourced from
          <code className="text-slate-500 ml-1">crm_stages</code>. Configure automations in
          <Link to="/lara/pipeline-workflows" className="text-purple-300 hover:underline ml-1">Lara AI › Pipeline Workflows</Link>.
        </div>

        <PipelineQuotesPeek
          deal={peekDeal}
          onClose={() => setPeekDeal(null)}
          onChanged={() => load()}
        />

        <Person360Overlay personId={person360Id} onClose={() => setPerson360Id(null)} />
        <QuotePeekModal
          quoteId={peekQuoteId}
          open={!!peekQuoteId}
          onClose={() => setPeekQuoteId(null)}
        />
        {bulkActivityOpen && (
          <BulkActivityModal personIds={[...selectedIds]} onClose={() => setBulkActivityOpen(false)}
            onSaved={() => { setBulkActivityOpen(false); clearSelection(); load(); }} />
        )}
      </div>
    </Layout>
    </NotesPeekProvider>
    </QuotePeekCtx.Provider>
  );
}
